import React, {ReactNode} from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {EndlessList, mapListConfig} from 'components/ui-kit/EndlessList2.0';
import {
    Gate,
    filtersChanged,
    listState$,
    sortingsChanged,
    filtersSubmit,
    filtersReset,
} from 'models/registries/gtinsGrid/list';
import {GtinsAccessors, GtinsGridFormattedRow, GtinsGridRawRow} from 'types/gtins';

import {Controls} from '../Controls';
import {GridsNavigation} from '../GridsNavigation';
import {ControlsWrapper} from '../Table/styled';

import {FlexRowElement, ListRow, Text} from './styled';

interface Props {
    configFunc: (t: TFunction) => Array<any>;
    dataMapper: (data: GtinsGridRawRow[] | undefined, t: TFunction) => GtinsGridFormattedRow[];
    headerControls?: ReactNode;
    exportConfig?: {
        exportPending: boolean;
        callback: Function;
    };
    showConfigBlock?: boolean;
}

export const GtinsGridList = ({configFunc, dataMapper, headerControls, exportConfig}: Props) => {
    const {t} = useTranslation();
    const {data, sortings, filters, pageIncremented, showConfigBlock} = useStore(listState$);
    const mappedData = React.useMemo(() => dataMapper(data?.content, t), [dataMapper, data, t]);
    const defaultHeaderControls = (
        <ControlsWrapper>
            <Controls />
            <GridsNavigation />
        </ControlsWrapper>
    );

    return (
        <>
            <Gate />
            <EndlessList<GtinsGridFormattedRow>
                rows={mappedData}
                hasMore={data?.hasNext}
                headerControls={headerControls ?? defaultHeaderControls}
                renderRow={(row: GtinsGridFormattedRow) => (
                    <ListRow>
                        <FlexRowElement>
                            <Text>{row[GtinsAccessors.brand]}</Text>
                        </FlexRowElement>
                        <FlexRowElement>
                            <Text>{row[GtinsAccessors.gtin]}</Text>
                        </FlexRowElement>
                        <FlexRowElement>
                            <Text>{row[GtinsAccessors.name]}</Text>
                        </FlexRowElement>
                        <FlexRowElement>
                            <Text isGray>{row[GtinsAccessors.manufacturer]}</Text>
                        </FlexRowElement>
                    </ListRow>
                )}
                configurationBlock={mapListConfig(
                    configFunc(t),
                    filters,
                    sortings,
                    (value) => filtersChanged([value]),
                    filtersSubmit,
                    filtersReset,
                    sortingsChanged,
                    showConfigBlock,
                )}
                onNextPage={() => pageIncremented()}
                exportConfig={exportConfig}
            />
        </>
    );
};
